import { useState, useRef } from 'react';
import { Upload, FileText, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUploadDocument } from './useDocuments';
import {
  ALLOWED_DOCUMENT_MIME_TYPES,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_OPTIONS,
  MAX_DOCUMENT_SIZE_BYTES,
  MAX_DOCUMENT_SIZE_LABEL,
} from './types';
import type { DocumentType } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTransactionId?: string | null;
}

export function DocumentUploadModal({ open, onOpenChange, defaultTransactionId }: Props) {
  const upload = useUploadDocument();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('other');
  const [supplierName, setSupplierName] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setName('');
    setDocumentType('other');
    setSupplierName('');
    setDocumentDate('');
    setNotes('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (selected: File | null) => {
    setError(null);
    if (!selected) {
      setFile(null);
      return;
    }
    if (!ALLOWED_DOCUMENT_MIME_TYPES.includes(selected.type) && selected.type !== '') {
      setError(`Formato não suportado: ${selected.type || 'desconhecido'}`);
      return;
    }
    if (selected.size > MAX_DOCUMENT_SIZE_BYTES) {
      setError(`Arquivo excede ${MAX_DOCUMENT_SIZE_LABEL}.`);
      return;
    }
    setFile(selected);
    if (!name.trim()) {
      setName(selected.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Selecione um arquivo.');
      return;
    }
    try {
      await upload.mutateAsync({
        file,
        name,
        documentType,
        supplierName,
        documentDate,
        notes,
        transactionId: defaultTransactionId ?? null,
      });
      reset();
      onOpenChange(false);
    } catch {
      // erro já mostrado via toast
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !upload.isPending) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">Enviar documento</DialogTitle>
          <DialogDescription>
            PDF, imagens, planilhas e textos. Máximo {MAX_DOCUMENT_SIZE_LABEL} por arquivo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* File picker */}
          {file ? (
            <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/30 p-3">
              <FileText size={20} className="mt-0.5 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024 / 1024).toFixed(2)} MB · {file.type || '—'}
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => handleFileChange(null)}
                disabled={upload.isPending}
              >
                <X size={14} />
              </Button>
            </div>
          ) : (
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border bg-secondary/20 px-4 py-6 transition-colors hover:bg-secondary/40">
              <Upload size={22} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                Clique para selecionar arquivo
              </span>
              <span className="text-xs text-muted-foreground">
                PDF, imagem, planilha, doc — até {MAX_DOCUMENT_SIZE_LABEL}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={ALLOWED_DOCUMENT_MIME_TYPES.join(',')}
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </label>
          )}

          {error && (
            <p className="rounded-md border border-expense/30 bg-expense/5 p-2 text-xs text-expense">
              {error}
            </p>
          )}

          {/* Nome */}
          <div className="space-y-1.5">
            <Label htmlFor="doc-name">Nome / título</Label>
            <Input
              id="doc-name"
              placeholder="Ex: NF agosto 2026 — fornecedor X"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={upload.isPending}
            />
          </div>

          {/* Tipo + Data lado a lado */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select
                value={documentType}
                onValueChange={(v) => setDocumentType(v as DocumentType)}
                disabled={upload.isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {DOCUMENT_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-date">Data do documento</Label>
              <Input
                id="doc-date"
                type="date"
                value={documentDate}
                onChange={(e) => setDocumentDate(e.target.value)}
                disabled={upload.isPending}
              />
            </div>
          </div>

          {/* Fornecedor */}
          <div className="space-y-1.5">
            <Label htmlFor="doc-supplier">Fornecedor / cliente</Label>
            <Input
              id="doc-supplier"
              placeholder="Ex: Distribuidora ABC"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              disabled={upload.isPending}
            />
          </div>

          {/* Notas */}
          <div className="space-y-1.5">
            <Label htmlFor="doc-notes">Observações (opcional)</Label>
            <Textarea
              id="doc-notes"
              rows={2}
              placeholder="Detalhes adicionais"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={upload.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={upload.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={upload.isPending || !file}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {upload.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <Upload size={14} className="mr-2" />
                Enviar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
